import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { eur, num, fmtTime } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { BirthdayReport } from "@/components/common/BirthdayReport";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell,
} from "recharts";
import { Loader2, TrendingUp, Wallet, Clock, Users, CheckCircle2 } from "lucide-react";

const gold = "hsl(var(--hi-gold))";

export default function Dashboard() {
  const nav = useNavigate();
  const [period, setPeriod] = useState("month");
  const [compare, setCompare] = useState("previous");
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState([]);
  const [today, setToday] = useState([]);
  const [booking, setBooking] = useState(null);
  const [source, setSource] = useState("all");
  const [metric, setMetric] = useState("revenue");
  const [drillDay, setDrillDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forecast, setForecast] = useState(null);

  // Previsione incasso di oggi: aggiornata in tempo reale (polling 30s) e a ogni
  // ritorno di focus, così riflette aggiunte/modifiche/cancellazioni/completamenti.
  useEffect(() => {
    let alive = true;
    const loadForecast = () => api.get("/dashboard/today-forecast").then((r) => { if (alive) setForecast(r.data); }).catch(() => {});
    loadForecast();
    const iv = setInterval(loadForecast, 30000);
    const onFocus = () => loadForecast();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);

  // Copia ordinata non mutante (evita di mutare lo stato con .sort in render)
  const todaySorted = useMemo(
    () => [...today].sort((a, b) => a.start.localeCompare(b.start)),
    [today]
  );

  const reload = () => {
    api.get(`/dashboard?period=${period}&compare=${compare}&source=${source}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => {
    setLoading(true);
    api.get(`/dashboard?period=${period}&compare=${compare}&source=${source}`).then((r) => setData(r.data)).finally(() => setLoading(false));
    api.get(`/booking/analytics?period=${period}`).then((r) => setBooking(r.data)).catch(() => {});
  }, [period, compare, source]);

  useEffect(() => {
    api.get("/goals").then((r) => setGoals(r.data)).catch(() => {});
    const start = new Date(); start.setHours(0,0,0,0);
    const end = new Date(); end.setHours(23,59,59,999);
    api.get(`/appointments?start=${start.toISOString()}&end=${end.toISOString()}`).then((r) => setToday(r.data)).catch(() => {});
  }, []);

  const k = data?.kpis || {};
  const fmtLabel = (s) => period === "year" ? s : s?.slice(8);

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Panoramica delle performance del salone">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[140px]" data-testid="dashboard-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Oggi</SelectItem>
            <SelectItem value="week">Settimana</SelectItem>
            <SelectItem value="month">Mese</SelectItem>
            <SelectItem value="quarter">Trimestre</SelectItem>
            <SelectItem value="year">Anno</SelectItem>
          </SelectContent>
        </Select>
        <Tabs value={compare} onValueChange={setCompare}>
          <TabsList>
            <TabsTrigger value="previous" data-testid="compare-previous">vs Precedente</TabsTrigger>
            <TabsTrigger value="year" data-testid="compare-year">vs Anno prec.</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[160px]" data-testid="dashboard-source-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i dati</SelectItem>
            <SelectItem value="native">Solo nativi</SelectItem>
            <SelectItem value="imported">Solo importati</SelectItem>
          </SelectContent>
        </Select>
      </PageHeader>

      {loading || !data ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--hi-gold))]" /></div>
      ) : (
        <div className="space-y-5">
          {/* KPI PRINCIPALI: gerarchia chiara, 4 metriche chiave */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard testid="kpi-revenue" label="Fatturato (netto)" value={eur(k.revenue?.value)} delta={k.revenue?.delta} hint={`Prec. ${eur(k.revenue?.prev)}`} />
            <KpiCard testid="kpi-collected" label="Incassato" value={eur(k.collected?.value)} delta={k.collected?.delta} hint={k.to_collect?.value ? `Da incassare ${eur(k.to_collect?.value)}` : undefined} />
            <KpiCard testid="kpi-clients" label="Clienti serviti" value={num(k.clients?.value)} delta={k.clients?.delta} hint={`${num(k.new_clients?.value)} nuovi`} />
            <KpiCard testid="kpi-avg-ticket" label="Ticket medio" value={eur(k.avg_ticket?.value)} delta={k.avg_ticket?.delta} />
          </div>

          {/* PREVISIONE INCASSO DI OGGI — aggiornata in tempo reale */}
          {forecast && (
            <Card className="p-5 border-[hsl(var(--hi-gold)/0.35)]" data-testid="today-forecast-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><TrendingUp className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Previsione incasso di oggi</div>
                  <div className="mt-1 text-3xl font-heading tabular" data-testid="forecast-total">{eur(forecast.forecast_total)}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Totale stimato della giornata (reale + previsto)</div>
                </div>
                <div className="flex-1 min-w-[220px] max-w-md">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>{forecast.appointments_completed} completati</span>
                    <span>{forecast.appointments_total} previsti</span>
                  </div>
                  <Progress value={forecast.appointments_total ? (forecast.appointments_completed / forecast.appointments_total) * 100 : 0} className="h-2" />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Wallet className="h-3.5 w-3.5" /> Incassato reale</div>
                  <div className="text-lg font-medium tabular mt-0.5" data-testid="forecast-real">{eur(forecast.real_collected)}</div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Ancora previsto</div>
                  <div className="text-lg font-medium tabular mt-0.5" data-testid="forecast-pending">{eur(forecast.still_expected)}</div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" /> Clienti previsti</div>
                  <div className="text-lg font-medium tabular mt-0.5" data-testid="forecast-appts">{num(forecast.appointments_total)}</div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><CheckCircle2 className="h-3.5 w-3.5" /> Già completati</div>
                  <div className="text-lg font-medium tabular mt-0.5" data-testid="forecast-completed">{num(forecast.appointments_completed)}</div>
                </div>
              </div>
            </Card>
          )}
          {/* METRICHE SECONDARIE: striscia compatta (riduce altezza e spazio) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="secondary-metrics">
            {[
              ["kpi-gross", "Lordo", eur(k.gross?.value)],
              ["kpi-margin", "Margine", eur(k.margin?.value), `${k.margin_pct?.value || 0}%`],
              ["kpi-services", "Servizi", eur(k.services_revenue?.value)],
              ["kpi-products", "Prodotti", eur(k.products_revenue?.value)],
              ["kpi-occupancy", "Occupazione", `${k.occupancy?.value || 0}%`],
              ["kpi-future", "Prenotazioni future", eur(k.future_bookings?.value)],
            ].map(([tid, lbl, val, sub]) => (
              <div key={tid} data-testid={tid} className="rounded-xl border border-border bg-card px-3.5 py-3 hover:border-[hsl(var(--hi-gold)/0.45)] transition-colors">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground truncate">{lbl}</div>
                <div className="mt-0.5 font-heading text-lg leading-tight tabular">{val}{sub && <span className="ml-1 text-[11px] text-muted-foreground font-sans">{sub}</span>}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <Card className="lg:col-span-8 p-5">
              <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                <h3 className="font-heading text-xl font-semibold">Andamento finanziario</h3>
                <div className="flex gap-1">
                  {[["revenue", "Fatturato"], ["collected", "Incassato"], ["discounts", "Sconti"]].map(([key, lbl]) => (
                    <button key={key} onClick={() => setMetric(key)} data-testid={`chart-metric-${key}`}
                      className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${metric === key ? "bg-[hsl(var(--hi-gold)/0.15)] text-[hsl(var(--hi-gold))] border-[hsl(var(--hi-gold)/0.5)]" : "text-muted-foreground border-border hover:bg-accent"}`}>{lbl}</button>
                  ))}
                </div>
              </div>
              {drillDay && (
                <div className="mb-2 text-xs text-muted-foreground flex items-center gap-2" data-testid="chart-drilldown">
                  Dettaglio {fmtLabel(drillDay.date)}: Fatturato {eur(drillDay.revenue)} · Incassato {eur(drillDay.collected)} · Sconti {eur(drillDay.discounts)}
                  <button className="text-[hsl(var(--hi-gold))]" onClick={() => setDrillDay(null)}>chiudi</button>
                </div>
              )}
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data.series} margin={{ left: -18, right: 8, top: 6 }}
                  onClick={(e) => { if (e && e.activePayload && e.activePayload[0]) setDrillDay(e.activePayload[0].payload); }}>
                  <defs>
                    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={gold} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={gold} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtLabel} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={54} tickFormatter={(v) => `\u20ac${v}`} />
                  <Tooltip formatter={(v) => eur(v)} labelFormatter={fmtLabel} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, color: "hsl(var(--foreground))" }} />
                  <Area type="monotone" dataKey={metric} stroke={gold} strokeWidth={2} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card className="lg:col-span-4 p-5">
              <h3 className="font-heading text-xl font-semibold mb-4">Top servizi</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.top_services} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => eur(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} fill={gold} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {data.break_even && (
            <Card className="p-5" data-testid="break-even-card">
              <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                <h3 className="font-heading text-xl font-semibold">Punto di pareggio & Margini</h3>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Costi fissi mensili</Label>
                  <Input type="number" defaultValue={data.break_even.fixed_monthly || ""} className="w-32 h-8" placeholder="0"
                    data-testid="fixed-costs-input"
                    onBlur={async (e) => { try { await api.put("/settings", { fixed_costs_monthly: Number(e.target.value) || 0 }); toast.success("Costi fissi aggiornati"); reload(); } catch { toast.error("Errore"); } }} />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg bg-muted p-3"><div className="font-heading text-lg">{eur(k.margin?.value)}</div><div className="text-[11px] text-muted-foreground">Margine di contribuzione ({k.margin_pct?.value || 0}%)</div></div>
                <div className="rounded-lg bg-muted p-3"><div className="font-heading text-lg">{eur(data.break_even.fixed_period)}</div><div className="text-[11px] text-muted-foreground">Costi fissi (periodo)</div></div>
                <div className="rounded-lg bg-muted p-3"><div className="font-heading text-lg">{data.break_even.break_even_revenue != null ? eur(data.break_even.break_even_revenue) : "—"}</div><div className="text-[11px] text-muted-foreground">Fatturato di pareggio</div></div>
                <div className={`rounded-lg p-3 ${data.break_even.net_profit >= 0 ? "bg-[hsl(var(--hi-status-success)/0.12)]" : "bg-destructive/10"}`}><div className={`font-heading text-lg ${data.break_even.net_profit >= 0 ? "text-[hsl(var(--hi-status-success))]" : "text-destructive"}`}>{eur(data.break_even.net_profit)}</div><div className="text-[11px] text-muted-foreground">Utile netto stimato</div></div>
              </div>
              {data.break_even.break_even_revenue != null && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Progresso verso il pareggio</span>
                    <span className={`font-medium ${data.break_even.reached ? "text-[hsl(var(--hi-status-success))]" : "text-[hsl(var(--hi-gold))]"}`} data-testid="break-even-progress">{data.break_even.reached ? "Pareggio raggiunto ✓" : `${data.break_even.progress || 0}%`}</span>
                  </div>
                  <Progress value={Math.min(data.break_even.progress || 0, 100)} className="h-2.5" />
                </div>
              )}
              {!data.break_even.fixed_monthly && <p className="text-[11px] text-muted-foreground mt-3">Imposta i costi fissi mensili (affitto, stipendi, utenze…) per calcolare il punto di pareggio.</p>}
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <Card className="lg:col-span-7 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-semibold">Appuntamenti di oggi</h3>
                <button className="text-sm text-[hsl(var(--hi-gold))]" onClick={() => nav("/agenda")} data-testid="dashboard-goto-agenda">Apri agenda</button>
              </div>
              <div className="space-y-2 max-h-[320px] overflow-y-auto hi-scroll">
                {today.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Nessun appuntamento oggi.</div>}
                {todaySorted.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 rounded-lg border border-border p-3">
                    <div className="text-sm font-medium tabular w-12">{fmtTime(a.start)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.client_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{(a.service_names||[]).join(", ")}</div>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                ))}
              </div>
            </Card>

            <Card className="lg:col-span-5 p-5">
              <h3 className="font-heading text-xl font-semibold mb-4">Obiettivi</h3>
              <div className="space-y-4">
                {goals.length === 0 && <div className="text-sm text-muted-foreground">Nessun obiettivo attivo.</div>}
                {goals.map((g) => (
                  <div key={g.id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{g.title}</span>
                      <span className="text-muted-foreground tabular">{g.metric==="clients"||g.metric==="services"?num(g.current):eur(g.current)} / {g.metric==="clients"||g.metric==="services"?num(g.target):eur(g.target)}</span>
                    </div>
                    <Progress value={g.progress} className="h-2" />
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Top collaboratori</h4>
                <div className="space-y-2">
                  {data.top_operators.map((o) => (
                    <div key={o.name} className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: o.color || gold }} />
                      <span className="text-sm flex-1">{o.name}</span>
                      <span className="text-sm font-medium tabular">{eur(o.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          <BirthdayReport onNavigate={(id) => nav(`/clienti/${id}`)} />
        </div>
      )}
    </div>
  );
}
